import React from 'react';
import PropTypes from 'prop-types';
import { Button, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faFileInvoice,
  faFileLines,
  faReceipt,
} from '@fortawesome/free-solid-svg-icons';
import { formatCurrency, formatDate, abbreviateFrequency } from '../../../utils/formatters';
import {
  calculateFortnightlyAmount,
  getRequirementFortnightStatus,
  formatEffectiveRange,
  REQUIREMENT_DISPLAY_KINDS,
} from '../../../utils/requirementCalculations';
import useRequirementsMobileLayout from '../../../hooks/useRequirementsMobileLayout';

const getFrequencyLabel = (frequency) => {
  const labels = {
    weekly: 'Weekly',
    fortnightly: 'Fortnightly',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    yearly: 'Yearly',
  };
  return labels[frequency] || frequency;
};

const CompactActionButton = ({ tooltip, label, onClick, disabled, variant, icon }) => (
  <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
    <span className="requirements-compact-row-action-slot">
      <Button
        variant={variant}
        size="sm"
        className="requirements-compact-row-action-btn"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
      >
        <FontAwesomeIcon icon={icon} fixedWidth />
      </Button>
    </span>
  </OverlayTrigger>
);

const RequirementCompactRow = ({
  req,
  fortnightStart,
  fortnightEnd,
  fortnightIndex,
  showInactive,
  onEdit,
  onAdvancedGenerate,
  onViewBills,
  categoryIcon,
}) => {
  const isMobile = useRequirementsMobileLayout();
  const status =
    fortnightStart && fortnightEnd
      ? getRequirementFortnightStatus(req, fortnightStart, fortnightEnd)
      : 'active';
  const rowInactive = showInactive && status !== 'active';
  const isAllocation = req.display_kind === 'allocation';

  const kindLabel = REQUIREMENT_DISPLAY_KINDS[req.display_kind] || req.display_kind || 'Allocation';
  const kindVariant =
    req.display_kind === 'bill_actual'
      ? 'primary'
      : req.display_kind === 'bill_estimated'
        ? 'info'
        : 'secondary';

  const statusBadge = {
    active: { bg: 'success', label: 'Active' },
    not_yet: { bg: 'secondary', label: 'Not yet' },
    ended: { bg: 'warning', label: 'Ended', text: 'dark' },
  };
  const { bg, label, text } = statusBadge[status] || statusBadge.active;

  const flags = [];
  if (req.additive_to_total) flags.push('Additive');
  if (req.exclude_from_required_amount) flags.push('Excl. transfer');

  const fnHint =
    req.frequency !== 'fortnightly'
      ? ` (${formatCurrency(calculateFortnightlyAmount(req))}/fn)`
      : '';

  const actionButtons = (
    <>
      <CompactActionButton
        tooltip={
          fortnightIndex < 0
            ? 'Cannot edit requirements for past fortnights'
            : 'Edit requirement'
        }
        label={`Edit ${req.name}`}
        onClick={() => onEdit(req)}
        disabled={fortnightIndex < 0}
        variant="outline-primary"
        icon={faEdit}
      />
      {!isAllocation && (
        <>
          <CompactActionButton
            tooltip="Generate bill"
            label={`Generate bill for ${req.name}`}
            onClick={() => onAdvancedGenerate(req.id)}
            variant="outline-success"
            icon={faFileInvoice}
          />
          <CompactActionButton
            tooltip="View bills"
            label={`View bills for ${req.name}`}
            onClick={() => onViewBills(req)}
            variant="outline-info"
            icon={faReceipt}
          />
        </>
      )}
    </>
  );

  if (isMobile) {
    return (
      <div
        className={`requirements-compact-row requirements-compact-row--mobile${
          rowInactive ? ' requirements-compact-row-inactive' : ''
        }${req.is_account_base ? ' requirements-compact-row-base' : ''}`}
      >
        <div className="requirements-compact-row-mobile-main">
          <div className="requirements-compact-row-mobile-top">
            <div className="requirements-compact-row-mobile-title-row">
              <div className="requirements-compact-row-mobile-name">
                {req.description && (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        <div style={{ whiteSpace: 'pre-wrap' }}>{req.description}</div>
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon icon={faFileLines} className="requirements-compact-row-note-icon" />
                  </OverlayTrigger>
                )}
                <span className="requirements-compact-row-name">{req.name}</span>
                {categoryIcon && <span className="requirements-compact-row-category">{categoryIcon}</span>}
              </div>
              <div className="requirements-compact-row-mobile-badges">
                <Badge bg={kindVariant} className="requirements-compact-row-kind-badge">
                  {kindLabel}
                </Badge>
                {showInactive && (
                  <Badge bg={bg} text={text} className="requirements-status-badge">
                    {label}
                  </Badge>
                )}
              </div>
            </div>

            <div className="requirements-compact-row-mobile-amount">
              <span className="requirements-compact-row-amount">{formatCurrency(req.amount)}</span>
              <span className="requirements-compact-row-freq text-muted">
                {getFrequencyLabel(req.frequency)}
                {fnHint}
              </span>
              {req.is_account_base && (
                <span className="requirements-compact-row-base-label text-muted">Base (min)</span>
              )}
            </div>

            {flags.length > 0 && (
              <div className="requirements-compact-row-mobile-flags text-muted small">{flags.join(' · ')}</div>
            )}
          </div>

          <div className="requirements-compact-row-mobile-footer">
            {!isAllocation && (
              <div className="requirements-compact-row-mobile-next-due">
                {req.next_unpaid_date ? (
                  <>Next Due: {formatDate(req.next_unpaid_date)}</>
                ) : (
                  <span className="text-muted">No due date</span>
                )}
              </div>
            )}
            <div className="requirements-compact-row-mobile-effective text-muted">
              {formatEffectiveRange(req)}
            </div>
          </div>
        </div>

        <div className="requirements-compact-row-actions requirements-compact-row-actions--mobile">
          {actionButtons}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`requirements-compact-row${rowInactive ? ' requirements-compact-row-inactive' : ''}${
        req.is_account_base ? ' requirements-compact-row-base' : ''
      }`}
    >
      <div className="requirements-compact-row-body">
        <div className="requirements-compact-row-primary">
          <div className="requirements-compact-row-leading">
            {req.description && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{req.description}</div>
                  </Tooltip>
                }
              >
                <FontAwesomeIcon icon={faFileLines} className="requirements-compact-row-note-icon" />
              </OverlayTrigger>
            )}
            <span className="requirements-compact-row-name">{req.name}</span>
            {categoryIcon && <span className="requirements-compact-row-category">{categoryIcon}</span>}
            <Badge bg={kindVariant} className="requirements-compact-row-kind-badge">
              {kindLabel}
            </Badge>
            <Badge bg={bg} text={text} className="requirements-status-badge">
              {label}
            </Badge>
          </div>
          <div className="requirements-compact-row-trailing">
            <span className="requirements-compact-row-amount">{formatCurrency(req.amount)}</span>
            <span className="requirements-compact-row-freq text-muted">
              {getFrequencyLabel(req.frequency)}
            </span>
            {req.is_account_base && (
              <span className="requirements-compact-row-base-label text-muted">Base (min)</span>
            )}
            {req.frequency !== 'fortnightly' && (
              <span className="requirements-compact-row-fn-hint text-muted">
                ({formatCurrency(calculateFortnightlyAmount(req))}/fn)
              </span>
            )}
          </div>
        </div>

        {(flags.length > 0 || !isAllocation) && (
          <div className="requirements-compact-row-secondary">
            {flags.length > 0 && <span className="text-muted small">{flags.join(' · ')}</span>}
            {!isAllocation && (
              <span className="small">
                {req.next_unpaid_date ? (
                  <>Next Due: {formatDate(req.next_unpaid_date)}</>
                ) : (
                  <span className="text-muted">No due date</span>
                )}
              </span>
            )}
          </div>
        )}

        <div className="small text-muted requirements-compact-row-effective">
          {formatEffectiveRange(req)}
        </div>
      </div>

      <div className="requirements-compact-row-actions">{actionButtons}</div>
    </div>
  );
};

RequirementCompactRow.propTypes = {
  req: PropTypes.object.isRequired,
  fortnightStart: PropTypes.instanceOf(Date),
  fortnightEnd: PropTypes.instanceOf(Date),
  fortnightIndex: PropTypes.number.isRequired,
  showInactive: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onAdvancedGenerate: PropTypes.func.isRequired,
  onViewBills: PropTypes.func.isRequired,
  categoryIcon: PropTypes.node,
};

export default RequirementCompactRow;
